<template>
  <div class="w-full">
    <!-- The summary card carries this note from lg up; below lg this step has no summary card. -->
    <payment-unpaid-invoices-note
      v-if="hasUnpaidInvoices"
      class="mb-6 lg:hidden"
      :text="t('checkout/step/create/unpaid_invoices')"
    />

    <!-- TV-2188: in the Payment Components flow only — the hosted flow has
         left for Chargebee by the time this step renders. A fresh checkout
         after the move is what carries the balance arm. -->
    <checkout-balance-migration-box
      v-if="paymentComponentsEnabled && ready"
      class="mb-6"
      @migrated="startCheckout"
    />

    <!-- Asked here, not a step back: a retry would resend the refused data. -->
    <!-- The payment card owns the submit; a refusal gives it back to the form. -->
    <div
      v-if="showBillingAddressCard"
      class="mb-6 rounded-lg bg-depth-1 p-4 sm:p-8"
    >
      <checkout-profile-address-form
        ref="addressForm"
        :rejected="needsBillingAddress"
        :rejected-field="rejectedBillingField"
        :back-to="`/${locale}/checkout/${planId}/step/1`"
        @saved="handleBillingAddressSaved"
      />
    </div>

    <!-- The section card every checkout step uses (frontend-conventions §6). -->
    <checkout-section-card v-if="!needsBillingAddress">
      <div :class="paymentComponentsEnabled ? 'sm:p-3' : 'p-3 text-center'">
        <!-- ---------------------------------------------------------------- -->
        <!-- Legacy hosted-page flow (flag OFF): create the hosted page, then  -->
        <!-- redirect the user to chargebee. Mirror of the nightly behavior.   -->
        <!-- ---------------------------------------------------------------- -->
        <template v-if="!paymentComponentsEnabled">
          <template v-if="!errorMessage && legacyStatus !== 'redirect'">
            <div
              class="mx-auto size-12 rounded-full border-4 border-depth-3 border-t-brand animate-spin"
            ></div>
            <h2 class="mt-5 text-lg font-bold text-mouse uppercase">
              {{ t('checkout/step/create/creating/title') }}
            </h2>
            <p class="mt-2 text-rhino">
              {{ t('checkout/step/create/creating/subtitle') }}
            </p>
          </template>

          <template v-else-if="legacyStatus === 'redirect' && !errorMessage">
            <div
              class="mx-auto flex size-12 items-center justify-center rounded-full bg-success/10"
            >
              <svg
                class="size-6 text-success"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
            </div>
            <h2 class="mt-5 text-lg font-bold text-mouse uppercase">
              {{ t('checkout/step/create/redirect/title') }}
            </h2>
            <p class="mt-2 text-rhino">
              {{ t('checkout/step/create/redirect/subtitle') }}
            </p>
            <common-button
              v-if="legacyCheckoutUrl"
              :href="legacyCheckoutUrl"
              size="custom"
              class="mt-5 px-8 py-3"
            >
              {{ t('checkout/step/create/button/continue') }}
            </common-button>
          </template>

          <template v-else>
            <div
              class="mx-auto flex size-12 items-center justify-center rounded-full bg-danger-fg/20"
            >
              <svg
                class="size-6 text-danger-fg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h2 class="mt-5 text-lg font-bold text-mouse uppercase">
              {{ t('checkout/step/create/error/title') }}
            </h2>
            <p class="mt-2 text-danger-fg">{{ errorMessage }}</p>
            <div class="mt-5 flex flex-wrap justify-center gap-3">
              <common-button
                v-if="showRetry"
                size="custom"
                class="px-8 py-3"
                @click="createLegacyCheckout"
              >
                {{ t('checkout/step/create/button/retry') }}
              </common-button>
              <common-button
                variant="secondary"
                outline
                :to="`/${locale}/checkout/${planId}/step/1`"
                size="custom"
                class="px-8 py-3"
              >
                {{ t('checkout/step/create/button/return') }}
              </common-button>
            </div>
          </template>
        </template>

        <!-- ------------------------------------------------------------------ -->
        <!-- Payment Components flow (flag ON): inline chargebee.components +  -->
        <!-- saved-method picker.                                              -->
        <!-- ------------------------------------------------------------------ -->
        <!-- Bootstrap (still fetching the payment_intent + saved methods). -->
        <div v-else-if="!errorMessage && !ready">
          <div
            class="mx-auto size-12 rounded-full border-4 border-depth-3 border-t-brand animate-spin"
          ></div>
          <h2 class="mt-5 text-lg font-bold text-mouse uppercase">
            {{ t('checkout/step/create/creating/title') }}
          </h2>
          <p class="mt-2 text-rhino">
            {{ t('checkout/step/create/creating/subtitle') }}
          </p>
        </div>

        <!-- One list for every way to pay. The customer's own rows (account
           balance, saved methods) and chargebee's rows for a new method share
           the selection-card look of frontend-conventions §7/§8, so a saved
           Klarna and a new Klarna read as the same thing. Exactly one row is
           selected across both parts: picking a chargebee row selects 'NEW',
           picking one of ours remounts the chargebee form closed. -->
        <template v-else-if="!errorMessage && (paymentIntent || hasOwnRows)">
          <h2
            class="mb-5 flex items-center gap-2.5 text-xl font-semibold uppercase tracking-wide text-mouse"
          >
            <fa-icon
              :icon="faWallet"
              class="size-5 text-rhino"
              aria-hidden="true"
            />
            {{ t('checkout/step/create/payment_method/title') }}
          </h2>

          <div v-if="hasOwnRows" role="radiogroup" class="space-y-2">
            <!-- Account balance. Offered only when the server said it covers the
               total (balance_covers_total) — that answer is the server's to make,
               and SubmitCheckout re-checks it, so a balance spent between the two
               calls is refused there rather than half-charged here. A free order
               shows the same row as "No payment needed". -->
            <label v-if="balanceOffered" :class="optionCardClass('BALANCE')">
              <input
                v-model="selectedOption"
                type="radio"
                value="BALANCE"
                class="sr-only"
                :disabled="charging"
              />
              <payment-method-logo type="balance" />
              <span class="flex-1">
                {{
                  freeOrder
                    ? t('checkout/step/create/free/label')
                    : t('checkout/step/create/balance/label')
                }}
              </span>
              <span v-if="!freeOrder" class="text-rhino">
                {{ formattedBalanceOption }}
              </span>
            </label>
            <label
              v-for="method in savedMethods"
              :key="method.id"
              :class="optionCardClass(method.id)"
            >
              <input
                v-model="selectedOption"
                type="radio"
                :value="method.id"
                class="sr-only"
                :disabled="charging"
              />
              <payment-method-logo
                :type="method.type"
                :brand="
                  method.details.case === 'card'
                    ? method.details.value.brand
                    : undefined
                "
              />
              <span class="flex min-w-0 flex-1 flex-col">
                <span>{{ savedMethodLabel(method) }}</span>
                <span
                  v-if="savedMethodDetail(method)"
                  class="truncate text-rhino"
                >
                  {{ savedMethodDetail(method) }}
                </span>
              </span>
              <!-- The roles Chargebee gives saved sources: renewals are
                   collected from the primary, the backup is tried when the
                   primary fails. -->
              <span
                v-if="method.isPrimary || method.isBackup"
                class="shrink-0 rounded-md border px-2 py-0.5 text-sm font-semibold uppercase tracking-wider"
                :class="
                  method.isPrimary
                    ? 'border-brand/40 text-brand'
                    : 'border-depth-3 text-rhino'
                "
              >
                {{
                  method.isPrimary
                    ? t('checkout/step/create/saved_methods/primary')
                    : t('checkout/step/create/saved_methods/backup')
                }}
              </span>
            </label>
          </div>

          <!-- Divider into chargebee's rows; only needed when ours are above. -->
          <div
            v-if="hasOwnRows && paymentIntent"
            class="my-5 flex items-center gap-3 text-rhino"
          >
            <span class="h-px flex-1 bg-white/5" aria-hidden="true" />
            {{ t('checkout/step/create/new_method/divider') }}
            <span class="h-px flex-1 bg-white/5" aria-hidden="true" />
          </div>

          <!-- chargebee.components form. Always mounted so its rows stay visible
             as the "new method" half of the list; its own pay button only shows
             while one of its rows is the selection. -->
          <chargebee-payment-form
            v-if="paymentIntent"
            :key="chargebeeFormKey"
            :payment-intent="paymentIntent"
            :subscription="klarnaSubscription"
            :billing-address="billingAddress"
            :on-submit="submitWithNewMethod"
            :submit-button-label="t('checkout/step/create/button/pay')"
            :cancel-hint="t('checkout/step/create/payment_window_hint')"
            :on-button-click="guardBeforeCharge"
            :submit-disabled="!termsAccepted"
            :disabled-hint="t('checkout/step/create/terms/required')"
            :select-default-payment-method="!hasOwnRows"
            :hide-submit="selectedOption !== 'NEW'"
            @method-change="onNewMethodOpened"
            @error="onError"
            @close="onClose"
            @blocked="onTermsBlocked"
          >
            <template #before-button>
              <checkout-payment-notes :notes="paymentNotes" />
              <checkout-terms-consent
                v-model="termsAccepted"
                :error="termsError"
              />
            </template>
          </chargebee-payment-form>

          <!-- Our own pay button for the balance or a saved method, in the place
             chargebee's button takes when a new method is selected. -->
          <div v-if="selectedOption !== 'NEW'" class="mt-4 space-y-4">
            <checkout-payment-notes :notes="paymentNotes" />
            <checkout-terms-consent
              v-model="termsAccepted"
              :error="termsError"
            />
            <common-button
              class="w-full"
              :disabled="charging || !termsAccepted"
              @click="submitSelectedOption"
            >
              {{
                submittingSaved
                  ? t('checkout/step/create/status/creating')
                  : t('checkout/step/create/button/pay')
              }}
            </common-button>
          </div>
        </template>

        <!-- Error -->
        <template v-else>
          <div>
            <div
              class="mx-auto flex size-12 items-center justify-center rounded-full bg-danger-fg/20"
            >
              <svg
                class="size-6 text-danger-fg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h2 class="mt-5 text-lg font-bold text-mouse uppercase">
              {{ t('checkout/step/create/error/title') }}
            </h2>
            <p class="mt-2 text-danger-fg">{{ errorMessage }}</p>
            <div class="mt-5 flex flex-wrap justify-center gap-3">
              <common-button
                v-if="showRetry"
                size="custom"
                class="px-8 py-3"
                @click="onRetry"
              >
                {{ t('checkout/step/create/button/retry') }}
              </common-button>
              <common-button
                variant="secondary"
                outline
                :to="`/${locale}/checkout/${planId}/step/1`"
                size="custom"
                class="px-8 py-3"
              >
                {{ t('checkout/step/create/button/return') }}
              </common-button>
            </div>
          </div>
        </template>
      </div>
    </checkout-section-card>
  </div>
</template>

<script setup lang="ts">
import {
  creditBalance as creditBalanceOf,
  creditDeduction as creditDeductionOf,
  promotionalDeduction as promotionalDeductionOf,
  remainingCredit,
} from '~/helpers/checkoutCredits';
import { captureException } from '@sentry/nuxt';
import { faWallet } from '@fortawesome/pro-regular-svg-icons';
import { Timestamp } from '@bufbuild/protobuf';
import {
  CheckoutConsent,
  SubmitCheckoutRequest,
} from 'gportal-grpc/gportal/api/payment/v1/requests_pb';
import type {
  CreateCheckoutPaymentIntentResponse,
  SavedPaymentMethod,
} from 'gportal-grpc/gportal/api/payment/v1/responses_pb';
import { Money } from 'gportal-grpc/gportal/type/v1/money_pb';
import ChargebeePaymentForm from '~/components/payment/ChargebeePaymentForm.vue';
import useAuth from '~/composables/useAuth';
import {
  ChargebeeSubmitBlocked,
  chargebeePaymentIntentFromProto,
} from '~/composables/useChargebeePayment';
import useFeatureFlag, { FeatureFlag } from '~/composables/useFeatureFlag';
import {
  type CheckoutError,
  type CheckoutErrorInfo,
  CHECKOUT_STORAGE_KEY,
  CHECKOUT_TOS_VERSION,
  getCheckoutErrorInfo,
  isHttpsUrl,
  isSoldOutError,
  isWrongRegionError,
  SECONDS_PER_DAY,
  wrongRegionTargetFromError,
} from '~/helpers/checkout';
import { trackBeginCheckout } from '~/helpers/gaEcommerce';

const { t, locale } = useI18n();
const route = useRoute();

const checkoutStore = useCheckoutStore();
const authStore = useAuthStore();
const router = useRouter();
const { login } = useAuth();
const { hasAccessToFeature, whenReady: whenFeatureFlagsReady } =
  useFeatureFlag();

// Picks between the two checkout flows. Read at mount time + on flag
// changes; the backend gates the new RPCs on the same flag so a stale
// "true" here just gets us a permission-denied that surfaces as the
// generic error message.
const paymentComponentsFlag = hasAccessToFeature(FeatureFlag.PaymentComponents);
// The server always reads the flag as off, and hydration keeps the server's classes, so read it only after mount.
const isMounted = ref(false);
const paymentComponentsEnabled = computed(
  () => isMounted.value && paymentComponentsFlag.value,
);

const planId = computed(() => route.params.planId as string);
const errorMessage = ref('');
const showRetry = ref(true);

// Legacy hosted-page flow state — only consulted when
// paymentComponentsEnabled is false.
const legacyStatus = ref<'idle' | 'creating' | 'redirect'>('idle');
const legacyCheckoutUrl = ref('');

// Filled by CreateCheckoutPaymentIntent. The intent always comes back so
// chargebee.components is ready to mount the moment the user picks "Add
// new"; saved methods are populated only for returning customers.
const paymentIntent = ref<Record<string, unknown> | null>(null);
// The id of the payment_intent CreateCheckoutPaymentIntent created. Even on
// the saved-method path (where the intent itself goes unused) its checkout
// record is what the subscription_created webhook reconciles against, so the
// result page polls GetCheckoutStatus by this id for both flows.
const createdPaymentIntentId = ref<string | null>(null);
const savedMethods = ref<SavedPaymentMethod[]>([]);

// TV-1875 §8 — the account-balance arm.
//
// All three come from CreateCheckoutPaymentIntent. `balanceCoversTotal` is the
// server's decision, not a comparison we make here: the same rule has to hold at
// submit time, and two implementations of it would disagree on rounding. The
// balance is only ever populated for a migrated customer, so an unmigrated one is
// never offered the arm.
const checkoutTotal = ref<Money | null>(null);
const availableBalance = ref<Money | null>(null);
const balanceCoversTotal = ref(false);
const { hasUnpaidInvoices } = useUnpaidInvoices(
  () => checkoutStore.price?.total?.currencyCode,
);

const { format: formatMoney } = useNumberMoneyFormatting();

// A free order (a 100 % coupon): nothing to collect, so no payment method is
// needed, migrated or not. The balance arm carries it — it sends no instrument —
// and SubmitCheckout lets a zero total through for every customer.
const freeOrder = computed(
  () =>
    balanceCoversTotal.value &&
    checkoutTotal.value != null &&
    BigInt(checkoutTotal.value.amount) === 0n,
);
// The arm is offered only when the server said the balance covers the total. A
// partial balance is still shown in the account, but offering it here would put a
// button in front of the customer that SubmitCheckout is bound to refuse.
const balanceOffered = computed(
  () =>
    balanceCoversTotal.value &&
    (availableBalance.value != null || freeOrder.value),
);
// Rows of our own above chargebee's: the balance and any saved method.
const hasOwnRows = computed(
  () => balanceOffered.value || savedMethods.value.length > 0,
);
// Any balance at all, covering the total or not. Chargebee applies it to the
// first invoice by itself, whichever method is selected, so the customer is
// told before paying.
// The balance row's figure, promotional credit included (see checkoutCredits).
const creditBalance = computed(() =>
  creditBalanceOf(
    availableBalance.value,
    checkoutStore.price?.accountBalance?.promotionalCredits,
  ),
);

const hasBalance = computed(
  () => (creditBalance.value ? BigInt(creditBalance.value.amount) : 0n) > 0n,
);

const formattedBalanceOption = computed(() =>
  creditBalance.value ? formatMoney(creditBalance.value) : '',
);
// The notes above the terms, for whatever is selected. BALANCE explains the
// deduction; a payment method with balance on the account says the balance is
// spent first; a new method says it becomes the primary one.
const paymentNotes = computed<string[]>(() => {
  if (selectedOption.value === 'BALANCE' && freeOrder.value) {
    return [t('checkout/step/create/free/hint')];
  }
  // The figures are the balance row's: what the credits pay of this purchase,
  // promotional credit included, and what is left of the row.
  if (selectedOption.value === 'BALANCE') {
    const promotional = promotionalDeduction.value;
    return [
      promotional && BigInt(promotional.amount) > 0n
        ? t('checkout/step/create/balance/covers_with_promo', {
            total: formattedTotal.value,
            promotional: formatMoney(promotional),
            remaining: formattedRemainingBalance.value,
          })
        : t('checkout/step/create/balance/covers', {
            total: formattedTotal.value,
            remaining: formattedRemainingBalance.value,
          }),
    ];
  }
  const notes: string[] = [];
  if (hasBalance.value) {
    notes.push(
      t('checkout/step/create/balance/applied_first', {
        amount: formattedBalanceOption.value,
      }),
    );
  }
  if (selectedOption.value === 'NEW') {
    notes.push(t('payment/form/new_method_primary_notice'));
  }
  return notes;
});

// Selection-card classes of frontend-conventions §8, for our own rows. The
// chargebee rows get the same look through style.rules in useChargebeePayment.
function optionCardClass(option: string): string[] {
  const selected = selectedOption.value === option;
  const classes = [
    'flex items-center gap-3 rounded-lg border-2 px-4 py-3 transition-all duration-200',
    selected
      ? 'border-brand bg-brand/10 text-brand'
      : 'border-depth-3 bg-depth-2 text-mouse',
  ];
  // Frozen while a charge is under way (see `charging`).
  if (charging.value) {
    classes.push('cursor-not-allowed opacity-60');
  } else {
    classes.push('cursor-pointer');
    if (!selected) classes.push('hover:border-brand hover:bg-brand/5');
  }
  return classes;
}

// Remounted to close an opened chargebee row when the customer goes back to one
// of ours: chargebee exposes no way to collapse its accordion from outside.
const chargebeeFormKey = ref(0);
let chargebeeRowOpen = false;

function onNewMethodOpened(): void {
  // A saved-method or balance charge is under way: its selection stays, and
  // chargebee's pay button with it hidden.
  if (charging.value) return;
  chargebeeRowOpen = true;
  selectedOption.value = 'NEW';
}

// The promotional credit this purchase uses, and what the credits pay of it
// together — the two credit lines of the price summary (see checkoutCredits).
const promotionalDeduction = computed(() =>
  promotionalDeductionOf(
    checkoutTotal.value,
    checkoutStore.price?.creditApplication,
  ),
);
const balanceDeduction = computed(() =>
  creditDeductionOf(
    checkoutTotal.value,
    checkoutStore.price?.creditApplication,
  ),
);

const formattedTotal = computed(() =>
  balanceDeduction.value ? formatMoney(balanceDeduction.value) : '',
);

// What is left after this purchase. Derived rather than asked for, because it is
// only ever shown next to the two figures it is derived from — and because the
// authoritative remaining balance is whatever Chargebee holds after the charge,
// which this page will not see.
const formattedRemainingBalance = computed(() => {
  const remaining = remainingCredit(
    creditBalance.value,
    balanceDeduction.value,
  );
  return remaining ? formatMoney(remaining) : '';
});
// The billing period of the subscription this checkout starts, for Klarna's
// recurring mandate. Durations are whole days, which is also how the
// Chargebee plans are priced ("Every 30 days").
const klarnaSubscription = computed(() => {
  const seconds = checkoutStore.selectedDuration;
  if (!seconds) return undefined;
  return {
    periodUnit: 'day' as const,
    period: Math.round(seconds / SECONDS_PER_DAY),
  };
});
// The customer's billing details from CreateCheckoutPaymentIntent, forwarded
// to the chargebee form's `context` so the billing form alternative methods
// render comes up prefilled. Card/PayPal ignore it.
const billingAddress = ref<{
  firstName?: string;
  lastName?: string;
  email?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zip?: string;
  countryCode?: string;
} | null>(null);
const intentRequest = ref<ReturnType<
  typeof checkoutStore.toCreateCheckoutPaymentIntentRequest
> | null>(null);

// Picker state. 'NEW' means "Add a new payment method" (shows the
// chargebee form), 'BALANCE' means pay out of the Chargebee account balance;
// anything else is a chargebee payment_source.id.
const selectedOption = ref<'NEW' | 'BALANCE' | string>('NEW');
// Leaving a new method for one of our rows: close chargebee's opened row by
// remounting the form, so only one row ever looks selected.
watch(selectedOption, (now, before) => {
  if (before === 'NEW' && now !== 'NEW' && chargebeeRowOpen) {
    chargebeeRowOpen = false;
    chargebeeFormKey.value += 1;
  }
});
const submittingSaved = ref(false);
// Set from the moment a charge starts until it fails; a success navigates away.
// While it is set the selection is frozen: picking one of our rows would remount
// the chargebee form under an authorized intent (dropping its error and the
// Retry against it), and our pay button would send a second SubmitCheckout
// under a different idempotency key — a second subscription.
const charging = ref(false);
const ready = ref(false);

// Set when Chargebee refused the billing data we hold.
const needsBillingAddress = ref(false);
// The Chargebee parameter that was refused, so the form can mark that field.
const rejectedBillingField = ref('');

const addressForm = ref<{
  submit: () => Promise<boolean>;
  isDirty: boolean;
} | null>(null);

// Whether the profile lacks what an invoice needs — the rule the Webinterface
// checkout asks for the address by (KeycloakUser::isComplete): first and last
// name and a country always; a postal code where the country wants one; street
// and city where it wants a full address. The country rules are the ones the
// address form itself validates with (useBillingCountries); until they have
// loaded only the always-required fields are judged, so a complete profile
// never flashes the form.
const {
  countriesWithPostalCode,
  countriesRequiringPostalCode,
  countriesRequiringFullAddress,
} = useBillingCountries();
const profileIncomplete = computed(() => {
  const user = authStore.user;
  const address = user?.address;
  if (!user?.firstName || !user?.lastName || !address?.country) return true;

  const country = address.country;
  const fullAddress = countriesRequiringFullAddress.value.includes(country);
  const postalCode =
    (countriesWithPostalCode.value.length === 0 ||
      countriesWithPostalCode.value.includes(country)) &&
    (countriesRequiringPostalCode.value.includes(country) || fullAddress);

  if (postalCode && !address.postalCode) return true;
  return fullAddress && (!address.locality || !address.street);
});

// Asked for only when it is needed: Chargebee refused the address we sent, or
// the profile is incomplete. Kept up across a payment error too, so typed
// input is never discarded.
const showBillingAddressCard = computed(
  () =>
    needsBillingAddress.value ||
    (paymentComponentsEnabled.value && ready.value && profileIncomplete.value),
);

// False when the form refused, so the caller aborts instead of charging.
async function saveBillingAddress(): Promise<boolean> {
  const form = addressForm.value;
  if (!showBillingAddressCard.value || form == null) return true;
  // Untouched and not refused: nothing to write, so do not gate the charge.
  if (!needsBillingAddress.value && !form.isDirty) return true;

  const saved = await form.submit();
  if (saved) {
    needsBillingAddress.value = false;
    rejectedBillingField.value = '';
  }

  return saved;
}

// Mandatory Terms & Conditions / Privacy consent shown next to the pay button.
// Legal requirement (the Payment Components flow dropped Chargebee's hosted-page
// agreements), so it gates BOTH submit paths and is sent to SubmitCheckout as
// the consent evidence trail persisted onto the subscription.
const termsAccepted = ref(false);
// Stamp of the moment the box was ticked — the accepted_at we record. Kept for
// the whole session (cleared if the user unticks) so it reflects the actual
// consent time, not the submit time.
const termsAcceptedAt = ref<Date | null>(null);
const termsError = ref('');

watch(termsAccepted, (accepted) => {
  if (accepted) {
    termsAcceptedAt.value ??= new Date();
    termsError.value = '';
  } else {
    termsAcceptedAt.value = null;
  }
});

// Consent proof sent with SubmitCheckout. Returns undefined when unchecked so
// we never fabricate consent — the gates below prevent submitting in that state
// anyway.
function buildConsent(): CheckoutConsent | undefined {
  if (!termsAccepted.value) return undefined;
  return new CheckoutConsent({
    termsAccepted: true,
    termsVersion: CHECKOUT_TOS_VERSION,
    acceptedAt: Timestamp.fromDate(termsAcceptedAt.value ?? new Date()),
  });
}

// Pre-submit gate for the chargebee pay button (new-method path): it checks the
// terms consent, then saves the billing address. Rejecting blocks chargebee from
// tokenizing/authorizing the intent, so a card is never charged without consent
// or with an address the form refused. The overlay + `inert` on
// ChargebeePaymentForm stop mouse/keyboard activation before this fires; this is
// the authoritative backstop (e.g. programmatic submit). Rejecting with
// ChargebeeSubmitBlocked marks it a validation block so the composable skips the
// payment-error toast and Sentry capture and we just show the inline message.
async function guardBeforeCharge(): Promise<void> {
  if (!termsAccepted.value) {
    termsError.value = t('checkout/step/create/terms/required');
    throw new ChargebeeSubmitBlocked('terms not accepted');
  }

  // The form shows its own error, so a refusal only has to block the charge.
  if (!(await saveBillingAddress())) {
    throw new ChargebeeSubmitBlocked('billing address rejected');
  }
}

// Fired when the user clicks the (overlay-blocked) chargebee button while
// consent is missing — surface the same inline message as the saved-method path.
function onTermsBlocked() {
  termsError.value = t('checkout/step/create/terms/required');
}

const buildAuthMetadata = useAuthMetadata();

// Persist payment_intent.id into the checkout store + raw localStorage.
// The /checkout/result page reads it back to poll GetCheckoutStatus once
// SubmitCheckout returns; we write to both targets to guard against a
// race where the store hasn't finished hydrating from initFromPlan when
// the user lands on the result page. Used by both the "NEW" and saved-method
// paths — the latter's subscription is reconciled to the same intent's
// checkout record by the webhook, so it polls just like the new-method flow.
function persistPaymentIntentId(paymentIntentId: string) {
  if (!paymentIntentId) return;

  // Clear the legacy counterpart so storage never holds both IDs at once.
  // result.vue prefers paymentIntentId, so a stale hostedPageId left behind
  // by a prior legacy attempt is harmless here — but a stale hostedPageId
  // would make the legacy flow poll the wrong checkout if the flag later
  // flips off, so keep the two mutually exclusive.
  checkoutStore.paymentIntentId = paymentIntentId;
  checkoutStore.hostedPageId = null;
  checkoutStore.persistToStorage();

  try {
    const raw = localStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (raw) {
      const config = JSON.parse(raw);
      config.paymentIntentId = paymentIntentId;
      delete config.hostedPageId;
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(config));
    }
  } catch {
    // ignore — store persistence above is the primary path.
  }
}

// Product names for the saved-source types Chargebee reports without display
// details. Brand names, so they are not translated. Matched exactly: `klarna`
// is Klarna via Stripe, `klarna_pay_now` older Adyen sources (labelled via
// their own i18n key in savedMethodLabel), and a future variant falling through to the
// generic label is merely ugly, whereas a wrong product name would mislead.
/* eslint-disable camelcase */
const SAVED_METHOD_PRODUCT_NAMES: Record<string, string> = {
  klarna: 'Klarna',
  alipay: 'Alipay',
  wechat_pay: 'WeChat Pay',
};
/* eslint-enable camelcase */

// The second line under a saved method's name, or '' for none. A PayPal row
// names the account it pays from, which is what tells two PayPal rows apart.
function savedMethodDetail(method: SavedPaymentMethod): string {
  if (method.details.case === 'paypal' && method.details.value.email) {
    return t('checkout/step/create/saved_methods/connected_account', {
      email: method.details.value.email,
    });
  }
  return '';
}

function savedMethodLabel(method: SavedPaymentMethod): string {
  switch (method.details.case) {
    case 'card': {
      const c = method.details.value;
      return t('checkout/step/create/saved_methods/card', {
        brand: prettyBrand(c.brand),
        last4: c.last4,
        month: c.expiryMonth.toString().padStart(2, '0'),
        year: String(c.expiryYear).slice(-2),
      });
    }
    case 'paypal':
      // The account goes on a second line, see savedMethodDetail.
      return 'PayPal';
    default:
      // Chargebee method types with no `details` variant modelled in the proto.
      // These sources carry no display detail — no last4, no email — so the
      // product name alone is the label. Without this the generic fallback
      // interpolates the raw chargebee type ("Gespeicherte wechat_pay").
      if (method.type === 'klarna_pay_now') {
        return t('checkout/step/create/saved_methods/klarna_pay_now');
      }
      if (SAVED_METHOD_PRODUCT_NAMES[method.type]) {
        return SAVED_METHOD_PRODUCT_NAMES[method.type];
      }

      return t('checkout/step/create/saved_methods/generic', {
        type: method.type || 'payment method',
      });
  }
}

function prettyBrand(brand: string): string {
  if (!brand) return '';
  // chargebee brands come in as e.g. "visa", "american_express", "mastercard"
  return brand
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

// TV-1789: on a cross-region rejection, flag the store (drives regionMismatch)
// with the location's home region as named by the backend, and send the user to
// the region-switch step (step 3). The step guard's redirect only fires on
// route/step changes, so flipping the flag on the current step would otherwise
// leave the user stuck on the generic failure state.
// A billing-address rejection is about the customer's own data: retrying sends
// exactly the same data and fails exactly the same way. Ask for a corrected
// address on this step instead of navigating anywhere (TV-1970).
//
// Kept in component state rather than the store: the refusal comes from the
// call this step makes, so a reload re-runs that call and re-derives it. There
// is nothing to persist, and nothing to clear on the way out.
function askForBillingAddress(info: CheckoutErrorInfo): void {
  if (info.redirectToProfileStep !== true) return;

  rejectedBillingField.value = info.rejectedField ?? '';
  needsBillingAddress.value = true;
}

// The refusal hid the payment card, so the form's own Save rebuilds it.
async function handleBillingAddressSaved(): Promise<void> {
  needsBillingAddress.value = false;
  rejectedBillingField.value = '';
  errorMessage.value = '';

  if (paymentComponentsEnabled.value) {
    await startCheckout();
  } else {
    await createLegacyCheckout();
  }
}

function steerToRegionSwitch(err: CheckoutError): void {
  checkoutStore.flagWrongRegion(wrongRegionTargetFromError(err));
  router.replace({
    params: { ...route.params, stepNumber: '3' },
  });
}

async function fetchPaymentIntent(): Promise<void> {
  const $grpcPayment = useGrpcPayment();
  const request = checkoutStore.toCreateCheckoutPaymentIntentRequest();
  intentRequest.value = request;

  const { response: resp, error: err } =
    await $grpcPayment.executeApiCallActive(
      'createCheckoutPaymentIntent',
      request,
      buildAuthMetadata(),
    );

  if (err != null) {
    if (isSoldOutError(err)) {
      // Pre-flag the location so the picker on step 1 already shows it as
      // sold-out when the user clicks "Return to configuration".
      checkoutStore.markLocationSoldOut(checkoutStore.selectedLocationId);
    } else if (isWrongRegionError(err)) {
      // TV-1789: the location is served only by the other region — steer the user
      // to the region-switch step (step 3) so they can switch and retry.
      steerToRegionSwitch(err);
    }
    const info = getCheckoutErrorInfo(err);
    showRetry.value = info.showRetry;
    askForBillingAddress(info);
    captureException(err, {
      extra: {
        context: 'StepCreate.createCheckoutPaymentIntent',
        code: err.code,
        details: err.details,
      },
    });
    throw new Error(t(info.messageKey));
  }

  const data = resp as unknown as CreateCheckoutPaymentIntentResponse;
  if (!data?.paymentIntent?.id) {
    captureException(
      new Error(
        '[StepCreate] CreateCheckoutPaymentIntent response missing payment_intent',
      ),
    );
    throw new Error(t('checkout/step/create/error/message'));
  }
  paymentIntent.value = chargebeePaymentIntentFromProto(data.paymentIntent);
  createdPaymentIntentId.value = data.paymentIntent.id;
  const ba = data.billingAddress;
  billingAddress.value = ba
    ? {
        firstName: ba.firstName,
        lastName: ba.lastName,
        email: ba.email,
        addressLine1: ba.addressLine1,
        city: ba.city,
        state: ba.state,
        zip: ba.zip,
        countryCode: ba.countryCode,
      }
    : null;
  // A fresh intent means any prior saved-method retry no longer applies.
  savedMethodRetryPending.value = false;
  savedMethods.value = data.savedPaymentMethods ?? [];
  checkoutTotal.value = data.total ?? null;
  availableBalance.value = data.availableBalance ?? null;
  balanceCoversTotal.value = data.balanceCoversTotal ?? false;

  // Preselect, in order: the account balance, the customer's primary saved
  // method, then "Add new" for new customers. The balance goes first because it
  // is money the customer has already paid us and cannot spend anywhere else —
  // leaving it unselected would charge a card while their own credit sat there.
  // The primary is picked by its flag; the server also orders it first
  // (orderSavedPaymentSources in the api), which is the fallback when no source
  // is flagged — a card added in the chargebee self portal lands at the end
  // instead of hijacking the preselection.
  if (balanceOffered.value) {
    selectedOption.value = 'BALANCE';
  } else {
    const primary =
      savedMethods.value.find((method) => method.isPrimary) ??
      savedMethods.value[0];
    selectedOption.value = primary ? primary.id : 'NEW';
  }
}

async function postSubmit(): Promise<void> {
  // SubmitCheckout has only created the Chargebee subscription; the
  // post-checkout redirect URL comes from GetCheckoutStatus polling on
  // the result page (which waits for the webhook + downstream
  // provisioning).
  // Awaited so the submitting flags stay set until the route has changed: the
  // result page's chunk can take a moment, and Pay must not come back meanwhile.
  await navigateTo(`/${locale.value}/checkout/result?state=succeeded`);
}

// Captures the payment_intent.id once chargebee.js has authorized it but
// before SubmitCheckout returns successfully. If SubmitCheckout fails
// (network blip, 5xx, timeout) we must NOT throw the user back into
// startCheckout — that would create a brand-new payment_intent and
// leave the previously-authorized one orphaned (duplicate auth hold,
// risk of duplicate subscription for non-unique plans). The Retry
// button re-runs SubmitCheckout with the same intent id instead.
const authorizedIntentId = ref<string | null>(null);

// Saved-method counterpart of authorizedIntentId. When a saved-method
// SubmitCheckout fails after the backend may have already acted, Retry must
// re-submit the SAME saved source against the SAME tracking intent
// (createdPaymentIntentId), not fall through to startCheckout() and mint a
// fresh payment_intent — that risks a duplicate submit / subscription for
// non-unique plans.
const savedMethodRetryPending = ref(false);

async function submitCheckoutForIntent(paymentIntentId: string): Promise<void> {
  if (!intentRequest.value) {
    throw new Error(t('checkout/step/create/error/message'));
  }
  const $grpcPayment = useGrpcPayment();
  const { error: err } = await $grpcPayment.executeApiCallActive(
    'submitCheckout',
    new SubmitCheckoutRequest({
      method: { case: 'paymentIntentId', value: paymentIntentId },
      order: intentRequest.value.order,
      consent: buildConsent(),
    }),
    buildAuthMetadata(),
  );
  if (err != null) {
    if (isSoldOutError(err)) {
      // Pre-flag the location so the picker on step 1 already shows it as
      // sold-out when the user clicks "Return to configuration".
      checkoutStore.markLocationSoldOut(checkoutStore.selectedLocationId);
    } else if (isWrongRegionError(err)) {
      // TV-1789: the location is served only by the other region — steer the user
      // to the region-switch step (step 3) so they can switch and retry.
      steerToRegionSwitch(err);
    }
    const info = getCheckoutErrorInfo(err);
    showRetry.value = info.showRetry;
    askForBillingAddress(info);
    captureException(err, {
      extra: {
        context: 'StepCreate.submitCheckout (new method)',
        code: err.code,
        details: err.details,
      },
    });
    throw new Error(t(info.messageKey));
  }
}

async function submitWithNewMethod(paymentIntentId: string): Promise<void> {
  charging.value = true;
  persistPaymentIntentId(paymentIntentId);
  authorizedIntentId.value = paymentIntentId;
  try {
    await submitCheckoutForIntent(paymentIntentId);
  } catch (err) {
    charging.value = false;
    throw err;
  }
  await postSubmit();
}

// The Pay button under the picker serves both non-card options. They differ only
// in the arm they put on the wire, so everything around the call — the consent
// gate, the inline-error handling, the retry against the same tracking intent —
// is shared rather than duplicated.
async function submitSelectedOption() {
  if (charging.value) return;
  charging.value = true;
  try {
    if (selectedOption.value === 'BALANCE') {
      await submitWithBalance();

      return;
    }

    await submitWithSavedMethod();
  } finally {
    charging.value = false;
  }
}

// TV-1875 §8. The arm carries no instrument at all; tracking_payment_intent_id is
// what links the resulting subscription to the checkout row the result page polls
// AND what makes the charge idempotent, so it is required rather than optional
// here — see SubmitCheckout's own refusal.
async function submitWithBalance() {
  if (!intentRequest.value || !createdPaymentIntentId.value) return;

  await submitPickerChoice(
    new SubmitCheckoutRequest({
      method: { case: 'payWithBalance', value: true },
      order: intentRequest.value.order,
      trackingPaymentIntentId: createdPaymentIntentId.value,
      consent: buildConsent(),
    }),
    'StepCreate.submitCheckout (balance)',
  );
}

async function submitWithSavedMethod() {
  if (!intentRequest.value || selectedOption.value === 'NEW') return;

  await submitPickerChoice(
    new SubmitCheckoutRequest({
      method: {
        case: 'existingPaymentSourceId',
        value: selectedOption.value,
      },
      order: intentRequest.value.order,
      // The intent we polled GetCheckoutStatus on. The oneof above carries
      // the saved source, not the intent, so the server needs this to link
      // the subscription to the exact checkout row the result page polls.
      trackingPaymentIntentId: createdPaymentIntentId.value ?? '',
      consent: buildConsent(),
    }),
    'StepCreate.submitCheckout (saved method)',
  );
}

// The shared submit for both picker options — a saved payment source and the
// account balance. Neither involves chargebee.components, so neither has an
// authorized intent to protect: a failure here is recoverable in place, and the
// Pay button is the retry against the same tracking intent.
async function submitPickerChoice(
  request: SubmitCheckoutRequest,
  sentryContext: string,
) {
  // Consent gate. The Pay button is also :disabled until the box is ticked;
  // this guards the programmatic entry points (e.g. Retry).
  if (!termsAccepted.value) {
    termsError.value = t('checkout/step/create/terms/required');
    return;
  }
  // Same one-button contract as the chargebee path: address first, then charge.
  if (!(await saveBillingAddress())) return;
  submittingSaved.value = true;
  errorMessage.value = '';
  // Mark this submit retryable against the same tracking intent (see onRetry).
  savedMethodRetryPending.value = true;

  try {
    const $grpcPayment = useGrpcPayment();
    const { error: err } = await $grpcPayment.executeApiCallActive(
      'submitCheckout',
      request,
      buildAuthMetadata(),
    );

    if (err != null) {
      if (isSoldOutError(err)) {
        // Pre-flag the location so the picker on step 1 already shows it as
        // sold-out when the user clicks "Return to configuration".
        checkoutStore.markLocationSoldOut(checkoutStore.selectedLocationId);
      } else if (isWrongRegionError(err)) {
        // TV-1789: location served only by the other region — steer the user to
        // the region-switch step (step 3) so they can switch and retry.
        steerToRegionSwitch(err);
      }
      const info = getCheckoutErrorInfo(err);
      showRetry.value = info.showRetry;
      askForBillingAddress(info);
      captureException(err, {
        extra: {
          context: sentryContext,
          code: err.code,
          details: err.details,
        },
      });
      errorMessage.value = t(info.messageKey);
      return;
    }

    // Persist the payment_intent id so the result page can poll
    // GetCheckoutStatus. SubmitCheckout linked the subscription to this
    // intent's checkout record synchronously (the subscription_created webhook
    // skips api-source subscriptions, so it can't do the match), via the
    // tracking_payment_intent_id sent above. Polling therefore waits for
    // downstream provisioning just like the new-method flow instead of showing
    // an unconfirmed success immediately.
    if (createdPaymentIntentId.value) {
      persistPaymentIntentId(createdPaymentIntentId.value);
    }
    savedMethodRetryPending.value = false;

    await postSubmit();
  } finally {
    submittingSaved.value = false;
  }
}

function onError(err: unknown) {
  captureException(err, {
    extra: { context: 'StepCreate.ChargebeePaymentForm' },
  });
  errorMessage.value =
    err instanceof Error
      ? err.message
      : t('checkout/step/create/error/message');
}

function onClose() {
  navigateTo(`/${locale.value}/checkout/result?state=cancelled`);
}

// createLegacyCheckout is the nightly-equivalent flow: call
// CreateCheckoutURL, persist the hosted_page_id so /checkout/result can
// poll GetCheckoutStatus on return, then redirect the browser to the
// Chargebee hosted page. Used only when `payment-components` is off.
async function createLegacyCheckout() {
  errorMessage.value = '';
  showRetry.value = true;
  legacyStatus.value = 'creating';
  legacyCheckoutUrl.value = '';

  const $grpcPayment = useGrpcPayment();
  const request = checkoutStore.toCreateCheckoutURLRequest();

  const { response: resp, error: err } =
    await $grpcPayment.executeApiCallActive(
      'createCheckoutURL',
      request,
      buildAuthMetadata(),
    );

  if (err != null) {
    if (isSoldOutError(err)) {
      // Pre-flag the location so the picker on step 1 already shows it as
      // sold-out when the user clicks "Return to configuration".
      checkoutStore.markLocationSoldOut(checkoutStore.selectedLocationId);
    } else if (isWrongRegionError(err)) {
      // TV-1789: the location is served only by the other region — steer the user
      // to the region-switch step (step 3) so they can switch and retry.
      steerToRegionSwitch(err);
    }
    const info = getCheckoutErrorInfo(err);
    errorMessage.value = t(info.messageKey);
    showRetry.value = info.showRetry;
    askForBillingAddress(info);
    captureException(err, {
      extra: {
        context: 'StepCreate.createCheckoutURL',
        code: err.code,
        details: err.details,
      },
    });
    return;
  }

  const response = (resp ?? {}) as {
    checkoutUrl?: string;
    hostedPageId?: string;
  };
  const url = response.checkoutUrl ?? '';
  if (url && !isHttpsUrl(url)) {
    errorMessage.value = t('checkout/step/create/error/message');
    return;
  }
  legacyCheckoutUrl.value = url;

  if (response.hostedPageId) {
    // Clear the new-flow counterpart so storage never holds both IDs at
    // once. result.vue prefers paymentIntentId, so a stale one left over
    // from a previous Payment Components attempt would otherwise make this
    // legacy redirect poll the wrong checkout on /checkout/result.
    checkoutStore.hostedPageId = response.hostedPageId;
    checkoutStore.paymentIntentId = null;
    checkoutStore.persistToStorage();

    // Belt-and-suspenders: write directly to localStorage too, mirroring
    // the persistPaymentIntentId guard in the new flow. Covers the
    // narrow window where a pending initFromPlan persist would
    // overwrite the freshly-set hostedPageId between us setting it on
    // the store and the user landing on /checkout/result.
    try {
      const raw = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      if (raw) {
        const config = JSON.parse(raw);
        config.hostedPageId = response.hostedPageId;
        delete config.paymentIntentId;
        localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(config));
      }
    } catch {
      // ignore — store persistence above is the primary path
    }
  }

  legacyStatus.value = 'redirect';

  if (legacyCheckoutUrl.value && isHttpsUrl(legacyCheckoutUrl.value)) {
    // GA4 begin_checkout — fired just before redirecting to the hosted
    // page (last in-app moment with store state intact).
    const total = checkoutStore.price?.total;
    const productKey = checkoutStore.productKey;
    if (total && productKey) {
      trackBeginCheckout({
        productKey,
        name: checkoutStore.product?.name ?? checkoutStore.product?.title,
        total,
        coupon: checkoutStore.couponCodes.join(',') || undefined,
      });
    }
    window.location.href = legacyCheckoutUrl.value;
  }
}

async function startCheckout() {
  errorMessage.value = '';
  showRetry.value = true;
  paymentIntent.value = null;
  savedMethods.value = [];
  billingAddress.value = null;
  ready.value = false;
  authorizedIntentId.value = null;
  try {
    // The price first: the notes and the credit figures read its credit
    // preview, and a balance migration (@migrated) changes it without anything
    // else asking again. A superseded or failed refresh would leave an older
    // price in place, so only a fresh one (null) lets the intent be created.
    if ((await checkoutStore.fetchPrice()) !== null) {
      throw new Error(t('checkout/step/create/error/message'));
    }
    await fetchPaymentIntent();
    ready.value = true;
    const total = checkoutStore.price?.total;
    const productKey = checkoutStore.productKey;
    if (total && productKey) {
      trackBeginCheckout({
        productKey,
        name: checkoutStore.product?.name ?? checkoutStore.product?.title,
        total,
        coupon: checkoutStore.couponCodes.join(',') || undefined,
      });
    }
  } catch (err) {
    captureException(err, {
      extra: { context: 'StepCreate.fetchPaymentIntent' },
    });
    errorMessage.value =
      err instanceof Error
        ? err.message
        : t('checkout/step/create/error/message');
  }
}

// Retry handler chooses between re-submitting an already-authorized
// intent and restarting checkout from scratch. The authorized-intent
// retry path is the important one: if chargebee.js already authorized
// the payment_intent but our SubmitCheckout call failed, restarting
// would orphan the auth hold and risk a duplicate subscription.
async function onRetry() {
  if (authorizedIntentId.value) {
    if (charging.value) return;
    charging.value = true;
    const intentId = authorizedIntentId.value;
    errorMessage.value = '';
    showRetry.value = true;
    try {
      await submitCheckoutForIntent(intentId);
      authorizedIntentId.value = null;
      await postSubmit();
    } catch (err) {
      charging.value = false;
      errorMessage.value =
        err instanceof Error
          ? err.message
          : t('checkout/step/create/error/message');
    }
    return;
  }
  if (savedMethodRetryPending.value) {
    // Re-submit the picker's choice against the same tracking intent instead of
    // minting a fresh one via startCheckout(): the backend may already have
    // acted, so a new intent risks a duplicate submit / subscription. That
    // holds for the balance arm as much as for a saved card — its idempotency
    // key IS the tracking intent, so re-submitting it is what makes a retry
    // free rather than a second charge.
    await submitSelectedOption();
    return;
  }
  // Save first: otherwise the retry sends the data that was just refused.
  if (!(await saveBillingAddress())) return;
  needsBillingAddress.value = false;
  rejectedBillingField.value = '';
  await startCheckout();
}

onMounted(async () => {
  isMounted.value = true;

  if (!authStore.isAuthenticated) {
    const redirectPath = `/${locale.value}/checkout/${planId.value}/step/4`;
    login(redirectPath);
    return;
  }

  // Safety net: if there's a region mismatch, redirect back to step 3
  // where the user can resolve it before proceeding.
  if (checkoutStore.regionMismatch) {
    router.replace({
      params: { ...route.params, stepNumber: '3' },
    });
    return;
  }

  // Wait for the initial targeting context before picking a flow. The
  // OpenFeature provider is ready by mount, but group/country targeting is
  // only in effect once the first setContext settles — reading the flag
  // before then would evaluate against the empty bootstrap context and send
  // an allow-listed user down the legacy path on a cold load.
  await whenFeatureFlagsReady();

  if (paymentComponentsEnabled.value) {
    await startCheckout();
  } else {
    await createLegacyCheckout();
  }
});
</script>
